import api from "../../../api/axiosInstance";
import { displayToast } from "../toast/actions";

const interventionDetailRequests = new Map();
const interventionAssessmentRequests = new Map();

// Create/Update Intervention (Upsert)
export const createOrUpdateIntervention = (patientId, interventionData) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/intervention/${patientId}`, interventionData);
      const data = response.data;
      
      if (interventionData.id === "00000000-0000-0000-0000-000000000000" || !interventionData.id) {
        // Create new intervention
        dispatch({ type: "CREATE_INTERVENTION_SUCCESS", payload: data });
        dispatch(displayToast('Intervention has been created successfully.', 'success', { autoClose: 4000 }));
      } else {
        // Update existing intervention
        dispatch({ type: "UPDATE_INTERVENTION_SUCCESS", payload: data });
        dispatch(displayToast('Intervention has been updated successfully.', 'success', { autoClose: 4000 }));
      }
      
      // Return the response data so the component can access the intervention ID
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save intervention. Please try again.';
      
      // Dispatch the appropriate failure action based on whether it's create or update
      if (interventionData.id === "00000000-0000-0000-0000-000000000000" || !interventionData.id) {
        dispatch({ type: "CREATE_INTERVENTION_FAILURE", payload: errorMessage });
      } else {
        dispatch({ type: "UPDATE_INTERVENTION_FAILURE", payload: errorMessage });
      }
      
      dispatch(displayToast(errorMessage, 'error', { autoClose: false }));
      throw error; // Re-throw the error so the component can handle it
    }
  };
};

// Search Interventions
export const searchInterventions = (patientId, filters = {}) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.interventionType) queryParams.append('interventionType', filters.interventionType);
      if (filters.clinicalServiceType) queryParams.append('clinicalServiceType', filters.clinicalServiceType);
      if (filters.clinicalServiceTypeCode) queryParams.append('clinicalServiceTypeCode', filters.clinicalServiceTypeCode);
      if (filters.createdDateFrom) queryParams.append('createdDateFrom', filters.createdDateFrom);
      if (filters.createdDateTo) queryParams.append('createdDateTo', filters.createdDateTo);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/interventions/${patientId}/search?${queryString}` : `/interventions/${patientId}/search`;
      
      const response = await api.get(url);
      const data = response.data;
      dispatch({ type: "FETCH_INTERVENTIONS_SUCCESS", payload: data });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch interventions. Please try again.';
      dispatch({ type: "FETCH_INTERVENTIONS_FAILURE", payload: errorMessage });
      dispatch(displayToast(errorMessage, 'error', { autoClose: 4000 }));
    }
  };
};

// Get Intervention by ID
export const getInterventionById = (patientId, id) => {
  return async (dispatch) => {
    const requestKey = `${patientId}:${id}`;
    if (interventionDetailRequests.has(requestKey)) {
      return interventionDetailRequests.get(requestKey);
    }

    const request = (async () => {
      try {
        dispatch(setLoading(true));
        const response = await api.get(`/intervention/${patientId}/${id}`);
        const data = response.data;
        dispatch({ type: "FETCH_INTERVENTION_BY_ID_SUCCESS", payload: data });
        return data;
      } catch (error) {
        const status = error.response?.status;
        // Axios may return response body as a string (e.g. BadRequest("...message...")) or as an object ({ message: "..." }); handle both.
        const responseBody = error.response?.data;
        const responseMessage = typeof responseBody === "string" ? responseBody : responseBody?.message || responseBody?.error || "";

        const errorMessage = responseMessage || "Failed to fetch intervention details. Please try again.";

        const messageLower = String(responseMessage || "").toLowerCase();

        // If the intervention doesn't exist (common when route is hit before creation), don't spam toast; just clear the right-panel state.
        const isNotFound = status === 404 || messageLower.includes("not found") || messageLower.includes("no intervention") || messageLower.includes("could not find") || messageLower.includes("does not exist");

        if (isNotFound) {
          dispatch({
            type: "FETCH_INTERVENTION_BY_ID_FAILURE",
            payload: { errorMessage: null, interventionId: id },
          });
          dispatch({ type: "CLEAR_CURRENT_INTERVENTION" });
          return null;
        }

        dispatch({
          type: "FETCH_INTERVENTION_BY_ID_FAILURE",
          payload: { errorMessage, interventionId: id },
        });
        dispatch(displayToast(errorMessage, "error", { autoClose: 4000 }));
        return null;
      } finally {
        interventionDetailRequests.delete(requestKey);
      }
    })();

    interventionDetailRequests.set(requestKey, request);
    return request;
  };
};

// Update Intervention Status
export const updateInterventionStatus = (patientId, id, status) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.patch(`/intervention/${patientId}/${id}/status`, { status });
      const data = response.data;
      dispatch({ type: "UPDATE_INTERVENTION_STATUS_SUCCESS", payload: { id, status: data.status } });
      dispatch(displayToast('Intervention status has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update intervention status. Please try again.';
      dispatch({ type: "UPDATE_INTERVENTION_STATUS_FAILURE", payload: errorMessage });
      dispatch(displayToast(errorMessage, 'error', { autoClose: false }));
    }
  };
};

// Update Intervention Assessment
export const updateInterventionAssessment = (id, assessmentData) => {
  return async (dispatch) => {
    const requestKey = `${id}:${JSON.stringify(assessmentData || {})}`;
    if (interventionAssessmentRequests.has(requestKey)) {
      return interventionAssessmentRequests.get(requestKey);
    }

    const request = (async () => {
      try {
        dispatch(setLoading(true));
        const response = await api.put(`/intervention/assessment/${id}`, assessmentData);
        const data = response.data;
        // Include the intervention ID in the payload so the reducer knows which intervention to update
        dispatch({ 
          type: "UPDATE_INTERVENTION_ASSESSMENT_SUCCESS", 
          payload: { ...data, interventionId: id } 
        });
        // dispatch(displayToast('Intervention assessment has been updated successfully.', 'success', { autoClose: 4000 }));
        return data;
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to update intervention assessment. Please try again.';
        dispatch({ type: "UPDATE_INTERVENTION_ASSESSMENT_FAILURE", payload: errorMessage });
        dispatch(displayToast(errorMessage, 'error', { autoClose: false }));
        return null;
      } finally {
        interventionAssessmentRequests.delete(requestKey);
      }
    })();

    interventionAssessmentRequests.set(requestKey, request);
    return request;
  };
};

// Set Search Filters
export const setSearchFilters = (filters) => {
  return async (dispatch) => {
    dispatch({ type: "SET_SEARCH_FILTERS", payload: filters });
  };
};

// Clear Search Filters
export const clearSearchFilters = () => {
  return async (dispatch) => {
    dispatch({ type: "CLEAR_SEARCH_FILTERS" });
  };
};

// Clear Current Intervention
export const clearCurrentIntervention = () => {
  return async (dispatch) => {
    dispatch({ type: "CLEAR_CURRENT_INTERVENTION" });
  };
};

// Clear Error
export const clearError = () => {
  return async (dispatch) => {
    dispatch({ type: "CLEAR_ERROR" });
  };
};

// Set Loading
export const setLoading = (isLoading) => {
  return async (dispatch) => {
    dispatch({ type: "SET_LOADING", payload: isLoading });
  };
};

// Fetch Clinical Service Data for Intervention
export const fetchClinicalServiceForIntervention = (clinicalServiceType, clinicalServiceId) => {
  return async (dispatch) => {
    try {
      // Validate inputs
      if (!clinicalServiceType || !clinicalServiceId) {
        dispatch({
          type: "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_FAILURE",
          payload: 'Missing clinical service information',
        });
        return null;
      }

      dispatch(setLoading(true));
      
      let response;
      const amrTypeNorm = String(clinicalServiceType || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const isMedicationReviewFetch =
        clinicalServiceType === "Medication Review" ||
        amrTypeNorm === "annualmedicationreview";
      if (isMedicationReviewFetch) {
        response = await api.get(`/annualMedicationReview/initialAssessment/${clinicalServiceId}`);
      } else if (clinicalServiceType === "Minor Ailment") {
        response = await api.get(`/initialAssessment/${clinicalServiceId}`);
      } else {
        const errorMessage = `Unknown clinical service type: ${clinicalServiceType}`;
        dispatch({
          type: "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_FAILURE",
          payload: errorMessage,
        });
        dispatch(displayToast(errorMessage, 'error', { autoClose: false }));
        return null;
      }
      
      const data = response.data;
      dispatch({ type: "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_SUCCESS", payload: data });
      return data;
    } catch (error) {
      const errorMessage = error.response?.data || error.response?.data?.message || error.message || 'Failed to fetch clinical service data';
      const statusCode = error.response?.status;
      
      if (statusCode === 400 || statusCode === 404) {
        dispatch({
          type: "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_FAILURE",
          payload: `Clinical service not found: ${typeof errorMessage === 'string' ? errorMessage : 'The associated clinical service could not be found'}`,
        });
      } else {
        dispatch({
          type: "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_FAILURE",
          payload: typeof errorMessage === 'string' ? errorMessage : 'Failed to fetch clinical service data',
        });
        dispatch(displayToast(`Failed to fetch clinical service data: ${typeof errorMessage === 'string' ? errorMessage : 'Please try again'}`, 'error', { autoClose: false }));
      }
      
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

// Reset Clinical Service Object
export const resetClinicalServiceForIntervention = () => {
  return async (dispatch) => {
    dispatch({ type: "RESET_CLINICAL_SERVICE_FOR_INTERVENTION" });
  };
};
